import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc,
  orderBy,
  runTransaction,
  Timestamp
} from 'firebase/firestore';
import { notifyNewOrder } from './notificationService';
import { db, auth } from '../firebase';

export interface OrderItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  size?: string;
  image?: string;
}

export interface CustomerDetails {
  name: string;
  surname: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode?: string;
  country?: string;
  identityNumber?: string;
}

// Siparisin tutar kalemleri.
// Eskiden siparis kaydinda yalnizca urun toplami vardi; kargo ucreti
// iyzico'ya gonderilen tutara ekleniyor ama kayda islenmiyordu. Bu yuzden
// musteriden tahsil edilen tutar ile kayitli tutar birbirini tutmuyordu.
export interface OrderTotals {
  subtotal: number;      // urunlerin toplami
  shippingCost: number;  // kargo ucreti
  discount: number;      // kupon indirimi
  total: number;         // musteriden tahsil edilen nihai tutar
  couponCode?: string;
}

export interface Order {
  id?: string;
  orderNumber: string;
  items: OrderItem[];
  customer: CustomerDetails;
  paymentId: string;
  conversationId?: string;
  totalAmount: number;   // tahsil edilen nihai tutar (kargo ve indirim dahil)
  subtotal?: number;
  shippingCost?: number;
  discount?: number;
  couponCode?: string;
  status: 'Alındı' | 'Hazırlanıyor' | 'Kargolandı' | 'Teslim Edildi' | 'İptal Edildi';
  createdAt: Date | Timestamp;
  updatedAt?: Date | Timestamp;
  cancellationReason?: string;
  cancelledAt?: Date | Timestamp;
  statusNote?: string;
  shippingAddress?: {
    name: string;
    surname: string;
    address: string;
    city: string;
    postalCode?: string;
    country?: string;
  };
  billingAddress?: {
    name: string;
    surname: string;
    address: string;
    city: string;
    postalCode?: string;
    country?: string;
  };
}

// Sipariş numarası oluşturma
export function generateOrderNumber(): string {
  // Siparis takibi giris istemiyor: numarayi bilen herkes o siparisi
  // goruntuleyebiliyor. Eski bicim "SM-" + 6 rakam idi; bu sadece 900.000
  // ihtimal demek ve sirayla denenerek tum musteri bilgileri toplanabilirdi.
  // Ayrica benzersizlik garantisi yoktu, ayni numara iki siparise dusebilirdi.
  //
  // Yeni bicim: SM-XXXXXXXXXX (10 karakter, karisikliga acik harfler cikarildi).
  // 32^10 civari ihtimal, kaba kuvvetle denenemez.
  const alphabet = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'; // 0/O ve 1/I yok
  const bytes = new Uint8Array(10);

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  let code = '';
  for (let i = 0; i < bytes.length; i++) {
    code += alphabet[bytes[i] % alphabet.length];
  }

  return `SM-${code}`;
}

// Siparis edilen adet kadar urun stogunu dusurur.
//
// Transaction kullaniliyor: iki musteri ayni anda son urunu alirsa okuma ve
// yazma arasindaki bosluktan faydalanip stogun eksiye dusmesini engelliyor.
// Stok asla 0'in altina inmez.
//
// Siparis zaten kaydedildikten SONRA cagrilir; burada bir hata olursa siparis
// iptal edilmez, sadece loglanir - musteri odemesini yapmistir.
async function decreaseProductStock(items: OrderItem[]): Promise<void> {
  for (const item of items) {
    if (!item.id) continue;

    try {
      await runTransaction(db, async (transaction) => {
        const productRef = doc(db, 'products', item.id);
        const snapshot = await transaction.get(productRef);

        if (!snapshot.exists()) return;

        const current = snapshot.data().stock;
        if (typeof current !== 'number') return;

        const next = Math.max(0, current - (item.quantity || 1));
        transaction.update(productRef, { stock: next });
      });
    } catch (error) {
      console.error(`Stok guncellenemedi (urun ${item.id}):`, error);
    }
  }
}

// Sipariş oluştur ve kaydet
export async function createOrder(
  cartItems: OrderItem[],
  customerDetails: CustomerDetails,
  paymentId: string,
  conversationId?: string,
  shippingAddress?: Order['shippingAddress'],
  billingAddress?: Order['billingAddress'],
  totals?: OrderTotals
): Promise<{ success: boolean; orderNumber?: string; orderId?: string; error?: string }> {
  try {
    const orderNumber = generateOrderNumber();
    
    // Tutar kalemleri. Odeme sayfasindan gelirse onlar esas alinir; gelmezse
    // en azindan urun toplami hesaplanir (eski davranisla uyum icin).
    const computedSubtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const subtotal = totals?.subtotal ?? computedSubtotal;
    const shippingCost = totals?.shippingCost ?? 0;
    const discount = totals?.discount ?? 0;
    const totalAmount = totals?.total ?? (subtotal + shippingCost - discount);

    const orderData: Omit<Order, 'id'> = {
      orderNumber: orderNumber,
      items: cartItems,
      customer: customerDetails,
      paymentId: paymentId,
      conversationId: conversationId,
      totalAmount: totalAmount,
      subtotal: subtotal,
      shippingCost: shippingCost,
      discount: discount,
      ...(totals?.couponCode ? { couponCode: totals.couponCode } : {}),
      status: 'Alındı',
      createdAt: Timestamp.now(),
      shippingAddress: shippingAddress,
      billingAddress: billingAddress,
    };

    const docRef = await addDoc(collection(db, 'orders'), orderData);
    console.log('✅ Sipariş başarıyla kaydedildi:', docRef.id, orderNumber);

    // Satilan adet kadar stogu dusur
    await decreaseProductStock(cartItems);

    // Site sahibine e-posta + Google Sheets tablosuna satir ekle.
    // notifyNewOrder asla hata firlatmaz; bildirim gitmese bile siparis gecerli.
    await notifyNewOrder({
      orderNumber,
      totalAmount,
      paymentId,
      customer: customerDetails,
      items: cartItems,
    });

    return { 
      success: true, 
      orderNumber: orderNumber,
      orderId: docRef.id
    };
  } catch (error: any) {
    console.error('❌ Sipariş kaydedilirken hata:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
}

// Sipariş sorgula (sipariş numarasına göre)
export async function trackOrder(orderNumber: string): Promise<Order | null> {
  try {
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef, where('orderNumber', '==', orderNumber));

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    let foundOrder: Order | null = null;
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      foundOrder = {
        id: docSnapshot.id,
        ...data,
        createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
          ? data.createdAt.toDate() 
          : (data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt)),
        updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
          ? data.updatedAt.toDate()
          : (data.updatedAt instanceof Date ? data.updatedAt : data.updatedAt ? new Date(data.updatedAt) : undefined),
      } as Order;
    });

    return foundOrder;
  } catch (error: any) {
    console.error('❌ Sipariş sorgulama hatası:', error);
    return null;
  }
}

// Tüm siparişleri getir (Admin için)
export async function getAllOrders(): Promise<Order[]> {
  try {
    const ordersRef = collection(db, 'orders');
    const q = query(ordersRef, orderBy('createdAt', 'desc'));

    const querySnapshot = await getDocs(q);
    const orders: Order[] = [];

    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      orders.push({
        id: docSnapshot.id,
        ...data,
        createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
          ? data.createdAt.toDate() 
          : (data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt)),
        updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
          ? data.updatedAt.toDate()
          : (data.updatedAt instanceof Date ? data.updatedAt : data.updatedAt ? new Date(data.updatedAt) : undefined),
      } as Order);
    });

    return orders;
  } catch (error: any) {
    console.error('❌ Siparişler getirilirken hata:', error);
    return [];
  }
}

// Sipariş durumu güncelle (Admin için)
export async function updateOrderStatus(
  orderId: string,
  newStatus: Order['status'],
  note?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const orderRef = doc(db, 'orders', orderId);

    const payload: Record<string, any> = {
      status: newStatus,
      updatedAt: Timestamp.now(),
    };

    // Iptal gerekcesi kayda gecer; musteriye giden bildirimde de yer alir.
    if (newStatus === 'İptal Edildi' && note && note.trim()) {
      payload.cancellationReason = note.trim();
      payload.cancelledAt = Timestamp.now();
    } else if (note && note.trim()) {
      payload.statusNote = note.trim();
    }

    await updateDoc(orderRef, payload);

    console.log(`✅ Sipariş durumu "${newStatus}" olarak güncellendi:`, orderId);
    return { success: true };
  } catch (error: any) {
    console.error('❌ Sipariş durumu güncellenirken hata:', error);
    return { success: false, error: error.message };
  }
}

// Siparisi iptal edip parayi iade eder.
//
// Islem sunucuda yapilir (api/payment/refund.js): iyzico gizli anahtari
// tarayiciya inmemeli ve iade yetkisi sunucu tarafinda dogrulanmali.
export async function requestRefund(
  orderId: string,
  paymentId: string,
  reason: string
): Promise<{ success: boolean; message?: string; error?: string; guidance?: string }> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return { success: false, error: 'Oturumunuz bulunamadı. Lütfen tekrar giriş yapın.' };
    }

    const token = await user.getIdToken();

    const response = await fetch('/api/payment/refund', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orderId, paymentId, reason }),
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      return {
        success: false,
        error: data.error || 'İade işlemi başarısız oldu.',
        guidance: data.guidance,
      };
    }

    return { success: true, message: data.message };
  } catch (error: any) {
    console.error('Iade istegi basarisiz:', error);
    return { success: false, error: error?.message || 'Bağlantı hatası' };
  }
}
